import {
	AccountBalanceWalletOutlined,
	AssuredWorkloadOutlined,
	BallotOutlined,
	CalendarMonthOutlined,
	EmojiEventsOutlined,
	InsertChartOutlined,
	LiveTvOutlined,
	NightlifeOutlined,
	PeopleAltOutlined,
  } from "@mui/icons-material";
  import { Avatar, Box, Grid, Pagination, Typography } from "@mui/material";
  import React, { useEffect } from "react";
  import { Helmet } from "react-helmet";
  import PropTypes from "prop-types";
  import Tabs from "@mui/material/Tabs";
  import Tab from "@mui/material/Tab";
  import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
  import api from "../../../services/Axios";
  import { useNavigate } from "react-router-dom";
  
  function CustomTabPanel(props) {
	const { children, value, index, ...other } = props;
  
	return (
	  <div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
		{...other}
	  >
		{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
	  </div>
	);
  }
  
  CustomTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
  };
  
  function a11yProps(index) {
	return {
	  id: `simple-tab-${index}`,
	  "aria-controls": `simple-tabpanel-${index}`,
	};
  }
  
  export default function Transaction() {
	const navigator = useNavigate();
	const [value, setValue] = React.useState(0);
  
	// ✅ NEW: search term state
	const [searchTerm, setSearchTerm] = React.useState("");
  
	// ✅ separate states (because API keys are different)
	const [eventCardData, setEventCardData] = React.useState({
	  totalVotes: 0,
	  totalEarning: 0,
	  djEarning: 0,
	  organizerEarning: 0,
	  adminEarning: 0,
	});
  
	const [tipsCardData, setTipsCardData] = React.useState({
	  totalTips: "0.00",
	  totalOrganizerTips: "0.00",
	  totalDjTips: "0.00",
	  totalAdminTips: "0.00",
	});
  
	const [eventListData, setEventListData] = React.useState([]);
	const [tipsListData, setTipsListData] = React.useState([]);
  
	const [pagination, setPagination] = React.useState({
	  totalRecords: 0,
	  totalPages: 0,
	  currentPage: 1,
	  perPage: 10,
	  hasNextPage: false,
	  hasPrevPage: false,
	});
  
	const handleChange = (event, newValue) => {
	  setValue(newValue);
	  // reset page when tab changes
	  setPagination((prev) => ({ ...prev, currentPage: 1 }));
	};
  
	const getAuthHeader = () => ({
	  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
	});
  
	// Tab 0 = Tips, Tab 1 = Event Earning
	const activeType = value === 0 ? "tips" : "event";
  
	// ✅ NEW: debounce (no css/class changes)
	const debounce = (func) => {
	  let timer;
	  return function (...args) {
		const context = this;
		if (timer) clearTimeout(timer);
		timer = setTimeout(() => {
		  timer = null;
		  func.apply(context, args);
		}, 500);
	  };
	};
  
	// ✅ NEW: search handler (reset page + set term)
	const handleChangeSearch = (e) => {
	  const v = e.target.value;
	  setPagination((prev) => ({ ...prev, currentPage: 1 }));
	  setSearchTerm(v);
	};
  
	// ✅ NEW: debounced input handler
	const optimizedVersion = React.useMemo(
	  () => debounce(handleChangeSearch),
	  []
	);
  
	const getTransactionList = async (page = 1, limit = 10, type = "event") => {
	  const header = getAuthHeader();
  
	  // ✅ NEW: send searchTerm in query (as `searchTerm`)
	  const q = `limit=${limit}&page=${page}&type=${type}&searchTerm=${encodeURIComponent(
		searchTerm || ""
	  )}`;
  
	  return api
		.get(`/event/earning?${q}`, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const getTransactionCard = async (type = "event") => {
	  const header = getAuthHeader();
	  return api
		.get(`/event/earning/dashboard?type=${type}`, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const getTransactionListData = async () => {
	  const response = await getTransactionList(
		pagination.currentPage,
		pagination.perPage,
		activeType
	  );
  
	  if (response?.data?.success) {
		const res = response?.data?.data?.data || [];
		const pag = response?.data?.data?.pagination || {};
  
		if (activeType === "tips") {
		  setTipsListData(res || []);
		} else {
		  setEventListData(res || []);
		}
  
		setPagination((prev) => ({
		  ...prev,
		  ...pag,
		}));
	  } else {
		if (activeType === "tips") setTipsListData([]);
		else setEventListData([]);
	  }
	};
  
	const getTransactionCardData = async () => {
	  const response = await getTransactionCard(activeType);
  
	  if (response?.data?.success) {
		// response?.data?.data contains the dashboard object
		const d = response?.data?.data || {};
  
		if (activeType === "tips") {
		  setTipsCardData({
			totalTips: d?.totalTips ?? "0.00",
			totalOrganizerTips: d?.totalOrganizerTips ?? "0.00",
			totalDjTips: d?.totalDjTips ?? "0.00",
			totalAdminTips: d?.totalAdminTips ?? "0.00",
		  });
		} else {
		  setEventCardData({
			totalVotes: d?.totalVotes ?? 0,
			totalEarning: d?.totalEarning ?? 0,
			djEarning: d?.djEarning ?? 0,
			organizerEarning: d?.organizerEarning ?? 0,
			adminEarning: d?.adminEarning ?? 0,
		  });
		}
	  } else {
		if (activeType === "tips") {
		  setTipsCardData({
			totalTips: "0.00",
			totalOrganizerTips: "0.00",
			totalDjTips: "0.00",
			totalAdminTips: "0.00",
		  });
		} else {
		  setEventCardData({
			totalVotes: 0,
			totalEarning: 0,
			djEarning: 0,
			organizerEarning: 0,
			adminEarning: 0,
		  });
		}
	  }
	};
  
	useEffect(() => {
	  getTransactionListData();
	  getTransactionCardData();
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, pagination.currentPage, searchTerm]); // ✅ include searchTerm
  
	const RenderCardsAndTable = () => {
	  const isTips = activeType === "tips";
	  const rows = isTips ? tipsListData : eventListData;
  
	  return (
		<>
		  <Helmet>
			<title>Transaction</title>
		  </Helmet>
  
		  <Box mb={2}>
			<div className="listingPagesHead">
			  <div className="right">
				<div className="rightInner">
				  <div className="searchGroup">
					<div>
					  <i className="material-icons">search</i>
					  <input
						type="text"
						className="form-control"
						placeholder="Search"
						id="searchTerm"
						name="searchTerm"
						defaultValue={searchTerm}
						onChange={optimizedVersion}
					  />
					</div>
				  </div>
				</div>
			  </div>
			</div>
		  </Box>
  
		  <Box mb={3}>
			<Grid container spacing={2}>
			  <Grid item xs={12} sm={4} md={3}>
				<Box borderRadius={2} p={2} sx={{ backgroundColor: "#112E55" }}>
				  <Grid
					item
					container
					justifyContent={"space-between"}
					alignItems={"center"}
				  >
					<Grid item>
					  <Typography color={"#fff"}>
						{isTips ? "Total Tips" : "Total Earning"}
					  </Typography>
					  <Typography fontSize={40} color={"#fff"} fontWeight={600}>
						{isTips
						  ? tipsCardData?.totalTips ?? "0.00"
						  : eventCardData?.totalEarning ?? 0}
					  </Typography>
					</Grid>
					<Grid item>
					  <AccountBalanceWalletOutlined
						sx={{ fontSize: 50, color: "#fff" }}
					  />
					</Grid>
				  </Grid>
				</Box>
			  </Grid>
  
			  <Grid item xs={12} sm={4} md={3}>
				<Box borderRadius={2} p={2} sx={{ backgroundColor: "#112E55" }}>
				  <Grid
					item
					container
					justifyContent={"space-between"}
					alignItems={"center"}
				  >
					<Grid item>
					  <Typography color={"#fff"}>
						{isTips ? "Organizer Tips" : "Organizer Earning"}
					  </Typography>
					  <Typography fontSize={40} color={"#fff"} fontWeight={600}>
						{isTips
						  ? tipsCardData?.totalOrganizerTips ?? "0.00"
						  : eventCardData?.organizerEarning ?? 0}
					  </Typography>
					</Grid>
					<Grid item>
					  <AccountBalanceWalletOutlined
						sx={{ fontSize: 50, color: "#fff" }}
					  />
					</Grid>
				  </Grid>
				</Box>
			  </Grid>
  
			  <Grid item xs={12} sm={4} md={3}>
				<Box borderRadius={2} p={2} sx={{ backgroundColor: "#112E55" }}>
				  <Grid
					item
					container
					justifyContent={"space-between"}
					alignItems={"center"}
				  >
					<Grid item>
					  <Typography color={"#fff"}>
						{isTips ? "Dj Tips" : "Dj Earning"}
					  </Typography>
					  <Typography fontSize={40} color={"#fff"} fontWeight={600}>
						{isTips
						  ? tipsCardData?.totalDjTips ?? "0.00"
						  : eventCardData?.djEarning ?? 0}
					  </Typography>
					</Grid>
					<Grid item>
					  <AccountBalanceWalletOutlined
						sx={{ fontSize: 50, color: "#fff" }}
					  />
					</Grid>
				  </Grid>
				</Box>
			  </Grid>
  
			  <Grid item xs={12} sm={4} md={3}>
				<Box borderRadius={2} p={2} sx={{ backgroundColor: "#112E55" }}>
				  <Grid
					item
					container
					justifyContent={"space-between"}
					alignItems={"center"}
				  >
					<Grid item>
					  <Typography color={"#fff"}>
						{isTips ? "Admin Tips" : "Admin Earning"}
					  </Typography>
					  <Typography fontSize={40} color={"#fff"} fontWeight={600}>
						{isTips
						  ? tipsCardData?.totalAdminTips ?? "0.00"
						  : eventCardData?.adminEarning ?? 0}
					  </Typography>
					</Grid>
					<Grid item>
					  <AccountBalanceWalletOutlined
						sx={{ fontSize: 50, color: "#fff" }}
					  />
					</Grid>
				  </Grid>
				</Box>
			  </Grid>
			</Grid>
		  </Box>
  
		  <div className="listingMain">
			<div className="customTable allaccountsTable table-responsive scrollbox">
			  <table className="table">
				<thead>
				  {isTips ? (
					<tr>
					  <th>Event Name</th>
					  <th className="w320px">Tip Amount</th>
					  <th>Organizer</th>
					  <th>Dj</th>
					  <th>Admin</th>
					</tr>
				  ) : (
					<tr>
					  <th>Event Name</th>
					  <th className="w320px">Total Vote</th>
					  <th>Earnings</th>
					  <th>Event Organizer</th>
					  <th>Dj</th>
					  <th>Admin</th>
					</tr>
				  )}
				</thead>
  
				<tbody>
				  {rows?.length > 0 ? (
					rows.map((item, idx) => (
					  <tr key={idx}>
						<td data-th="Event Name">
						  <Grid
							container
							spacing={1}
							alignItems={"center"}
							onClick={() => {
							  if (isTips) {
								navigator(`/user/event-details?id=${item?.eventId}`);
							  } else {
								navigator(`/user/event-details?id=${item?.id}`);
							  }
							}}
							style={{ cursor: !isTips ? "pointer" : "default" }}
						  >
							<Grid item>{/* <Avatar /> */}</Grid>
							<Grid item width={"calc(100% - 48px)"}>
							  <Typography color={"#0057ff"} fontWeight={500}>
								{item?.eventName} <ArrowForwardRoundedIcon />
							  </Typography>
							</Grid>
						  </Grid>
						</td>
  
						{isTips ? (
						  <>
							<td data-th="Tip Amount">$ {item?.amount ?? "0.00"}</td>
							<td data-th="Organizer">{item?.organizerShareAmount ?? "0.00"}</td>
							<td data-th="Dj">$ {item?.djShareAmount ?? "0.00"}</td>
							<td data-th="Admin">$ {item?.adminShareAmount ?? "0.00"}</td>
						  </>
						) : (
						  <>
							<td data-th="total vote">{item?.totalVotes ?? 0}</td>
							<td data-th="earnings">{item?.totalEarning ?? 0}</td>
							<td data-th="Event Organizer">{item?.organizerEarning ?? 0}</td>
							<td data-th="Dj">{item?.djEarning ?? 0}</td>
							<td data-th="Admin">{item?.adminEarning ?? 0}</td>
						  </>
						)}
					  </tr>
					))
				  ) : (
					<tr>
					  <td colSpan={isTips ? 5 : 6} style={{ textAlign: "center" }}>
						No records found
					  </td>
					</tr>
				  )}
				</tbody>
			  </table>
			</div>
  
			<Box mt={2} display="flex" justifyContent="flex-end">
			  <Pagination
				count={pagination.totalPages}
				page={pagination.currentPage}
				color="primary"
				onChange={(e, page) =>
				  setPagination((prev) => ({
					...prev,
					currentPage: page,
				  }))
				}
			  />
			</Box>
		  </div>
		</>
	  );
	};
  
	return (
	  <>
		<Helmet>
		  <title> Dashboard</title>
		</Helmet>
  
		<div className="listingPages allAccountsPage">
		  <Box maxWidth={1600} mx={"auto"}>
			<div className="listingPagesHead">
			  <div className="left">
				<h3>Transaction</h3>
			  </div>
			</div>
  
			<Box sx={{ borderBottom: 1, borderColor: "#0057FF" }} mb={3}>
			  <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
				<Tab label="Tips" {...a11yProps(0)} />
				<Tab label="Event Earning" {...a11yProps(1)} />
			  </Tabs>
			</Box>
  
			<CustomTabPanel value={value} index={0}>
			  <RenderCardsAndTable />
			</CustomTabPanel>
  
			<CustomTabPanel value={value} index={1}>
			  <RenderCardsAndTable />
			</CustomTabPanel>
		  </Box>
		</div>
	  </>
	);
  }
  